import { ContextFormFieldText } from '@bambi/component-library/src/components/forms/context/ContextFormFieldText';
import { DefaultFormFieldLayout } from '@bambi/component-library/src/components/forms/DefaultFormFieldLayout/DefaultFormFieldLayout';
import { CoreFormFieldTextArea } from '@bambi/component-library/src/components/forms/fields/CoreFormFieldTextArea';
import { PhoneInput } from '@bambi/component-library/src/components/inputs/PhoneInput';
import { Controller, useFormContext } from 'react-hook-form';

import {
  BillingSettingsRead,
  PatchedOrganizationSettings,
} from 'src/common/external/bambi-api/bambiApi';
import { FormFieldDefault } from 'src/common/FormField/FormFieldDefault';
import { SettingsRowHeader } from 'src/features/settings/OrganizationSettingsForm/components/SettingsRowHeader';

import {
  InvoiceColumnKey,
  InvoiceColumnSelect,
} from '../invoices/modals/DownloadInvoices/steps/components/InvoiceColumnSelect';
import { PaymentLinkTemplatePreview } from './components/PaymentLinkTemplatePreview';
import { SettingsFields } from './components/SettingsFields';
import { SettingsRow } from './components/SettingsRow';

import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';

export type BillingSettingsFormValues = BillingSettingsRead & {
  billing_address?: string;
  tax_id: PatchedOrganizationSettings['tax_id'];
  invoiceGenerationOptions: SelectOption<InvoiceColumnKey>[];
};

interface BillingSettingsFormProps {
  minimumInvoiceNumber: number;
}

export default function BillingSettingsForm({
  minimumInvoiceNumber,
}: BillingSettingsFormProps) {
  const { control, watch } = useFormContext<BillingSettingsFormValues>();

  const paymentLinkTemplateText = watch('payment_link_email_template');

  return (
    <div>
      <SettingsRow>
        <SettingsRowHeader
          title="Invoice"
          description="Auto-generating invoice number settings"
        />
        <SettingsFields>
          <Controller
            control={control}
            name="invoice_prefix"
            render={({ field }) => {
              return (
                <FormFieldDefault
                  inputProps={{
                    ...field,
                    value: field.value ?? '',
                    id: 'invoice_prefix',
                  }}
                  label="Invoice Prefix"
                  type="text"
                />
              );
            }}
          />
          <Controller
            control={control}
            name="invoice_seed_number"
            rules={{
              min: {
                value: minimumInvoiceNumber,
                message: 'Seed number cannot be lower than its current value',
              },
            }}
            render={({ field, fieldState }) => {
              return (
                <FormFieldDefault
                  inputProps={{
                    ...field,
                    value: field.value ?? '',
                    id: 'invoice_seed_number',
                  }}
                  error={fieldState.error?.message ?? ''}
                  label="Seed Number"
                  type="number"
                />
              );
            }}
          />
          <InvoiceColumnSelect
            fieldPath="invoiceGenerationOptions"
            label="Invoice Generation Columns"
          />
        </SettingsFields>
      </SettingsRow>
      <SettingsRow>
        <SettingsRowHeader
          title="&nbsp;"
          description="Billing contact information, appears on the invoice"
        />
        <SettingsFields>
          <Controller
            control={control}
            name="email"
            render={({ field }) => {
              return (
                <FormFieldDefault
                  inputProps={{
                    ...field,
                    value: field.value ?? '',
                    id: 'email',
                  }}
                  label="Billing Email"
                  type="email"
                />
              );
            }}
          />
          <Controller
            control={control}
            name="phone_number"
            render={({ field }) => {
              return (
                <DefaultFormFieldLayout
                  label="Billing Phone"
                  inputId="phone_number"
                >
                  <PhoneInput {...field} placeholder="(555) 555-5555" />
                </DefaultFormFieldLayout>
              );
            }}
          />
          <Controller
            control={control}
            name="billing_address"
            render={({ field }) => {
              return (
                <FormFieldDefault
                  inputProps={{
                    ...field,
                    value: field.value ?? '',
                    id: 'billing_address',
                  }}
                  label="Billing Address (replaces headquarters location on invoice)"
                  type="text"
                />
              );
            }}
          />
        </SettingsFields>
      </SettingsRow>

      <SettingsRow>
        <SettingsRowHeader
          title="&nbsp;"
          description="Additional invoice fields"
        />
        <SettingsFields>
          <Controller
            control={control}
            name="invoice_note"
            render={({ field }) => {
              return (
                <FormFieldDefault
                  inputProps={{
                    ...field,
                    value: field.value ?? '',
                    id: 'invoice_note',
                  }}
                  label="Invoice Header Note"
                  type="text"
                />
              );
            }}
          />
          <div>
            <ContextFormFieldText
              label="Tax ID"
              fieldPath="tax_id"
              placeholder="Tax ID for export"
            />
          </div>
        </SettingsFields>
      </SettingsRow>
      <SettingsRow>
        <SettingsRowHeader
          title="Payments"
          description="Payment link settings"
        />
        <SettingsFields>
          <Controller
            control={control}
            name="payment_link_email_template"
            render={({ field }) => {
              return (
                <CoreFormFieldTextArea
                  inputProps={{
                    ...field,
                    value: field.value ?? '',
                    id: 'payment_link_email_template',
                    rows: 11,
                  }}
                  helperText={{
                    text: 'Available data: {{invoice_number}}, {{organization_name}}, {{payment_link}}, {{billing_email}}, {{billing_phone}}',
                    presentation: {
                      type: 'tooltip',
                    },
                  }}
                  label="Payment Link Email Template"
                />
              );
            }}
          />
          <PaymentLinkTemplatePreview text={paymentLinkTemplateText} />
        </SettingsFields>
      </SettingsRow>
    </div>
  );
}
